import { useState } from 'react';
import Reveal from '@/components/base/Reveal';
import { commentItems } from '@/mocks/homeData';

const toneMap: Record<string, string> = {
  primary: 'bg-primary-100 text-primary-700',
  accent: 'bg-accent-100 text-accent-800',
  secondary: 'bg-secondary-100 text-secondary-900',
};

export default function CommentsSection() {
  const [liked, setLiked] = useState<Record<string, boolean>>({});
  const [draft, setDraft] = useState('');
  const [notice, setNotice] = useState('');

  const toggleLike = (id: string) => {
    setLiked((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handlePost = () => {
    if (!draft.trim()) {
      setNotice('先写点什么再发表吧');
    } else {
      setNotice('演示页面：评论仅作氛围展示，暂不提交真实内容');
    }
    window.setTimeout(() => setNotice(''), 2600);
  };

  return (
    <section id="discussion" className="w-full scroll-mt-24 border-y border-background-200/70 bg-secondary-50">
      <div className="mx-auto w-full max-w-[1200px] px-4 py-12 md:px-6 md:py-16">
        <Reveal>
          <div className="mb-8 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <span className="text-xs font-label tracking-[0.2em] text-secondary-600">DISCUSSION</span>
              <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">吃瓜讨论区</h2>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
                入口、线路、下载遇到的问题，这里都有人先踩过坑。看看大家怎么说，也留下你的经验。
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-secondary-200 bg-background-50 px-3 py-1.5 text-xs text-foreground-700">
                <i className="ri-chat-smile-2-line text-secondary-600" />
                今日 1,284 条讨论
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-secondary-200 bg-background-50 px-3 py-1.5 text-xs text-foreground-700">
                <i className="ri-user-3-line text-accent-600" />
                在线 3,562 人
              </span>
            </div>
          </div>
        </Reveal>

        <Reveal>
          <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-foreground-900 text-sm font-medium text-background-50">
                我
              </span>
              <div className="min-w-0 flex-1">
                <textarea
                  value={draft}
                  maxLength={500}
                  onChange={(e) => setDraft(e.target.value)}
                  rows={2}
                  placeholder="说说你今天的进场体验，或分享一条好用的线路…"
                  className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-300 focus:bg-background-50"
                />
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-xs text-foreground-400">{draft.length}/500</span>
                  <button
                    type="button"
                    onClick={handlePost}
                    className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-send-plane-line" />
                    发表
                  </button>
                </div>
              </div>
            </div>
            {notice && (
              <p className="mt-3 inline-flex items-center gap-2 rounded-md border border-accent-200 bg-accent-50 px-3 py-2 text-xs text-accent-800">
                <i className="ri-information-line" />
                {notice}
              </p>
            )}
          </div>
        </Reveal>

        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
          {commentItems.map((comment, index) => {
            const isLiked = Boolean(liked[comment.id]);
            return (
              <Reveal key={comment.id} delay={index * 50}>
                <article className="flex h-full flex-col rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:bg-background-100 md:p-5">
                  <div className="flex items-center gap-3">
                    <span
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-sm font-medium ${
                        toneMap[comment.tone] ?? toneMap.primary
                      }`}
                    >
                      {comment.user.slice(0, 1)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-foreground-900">{comment.user}</p>
                      <p className="text-xs text-foreground-400">{comment.time}</p>
                    </div>
                    <span className="rounded-full bg-background-200 px-2 py-0.5 text-[10px] text-foreground-600">已认证</span>
                  </div>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">{comment.text}</p>
                  <div className="mt-4 flex items-center gap-4 border-t border-background-200 pt-3">
                    <button
                      type="button"
                      onClick={() => toggleLike(comment.id)}
                      className={`inline-flex cursor-pointer items-center gap-1.5 text-xs transition-colors ${
                        isLiked ? 'text-primary-600' : 'text-foreground-500 hover:text-foreground-800'
                      }`}
                    >
                      <i className={isLiked ? 'ri-heart-fill' : 'ri-heart-line'} />
                      {comment.likes + (isLiked ? 1 : 0)}
                    </button>
                    <span className="inline-flex items-center gap-1.5 text-xs text-foreground-500">
                      <i className="ri-chat-3-line" />
                      {comment.replies} 条回复
                    </span>
                    <span className="ml-auto inline-flex items-center gap-1.5 text-xs text-foreground-400">
                      <i className="ri-share-forward-line" />
                      分享
                    </span>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}